
import { DeviceTypeSettings } from "@/api/dtos/DeviceTypeSettings.schema";
import { deepClone } from "@/misc/helpers";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import CancelButton from "../buttons/CancelButton.vue";
import DangerButton from "../buttons/DangerButton.vue";
import EditFrame from "../EditFrame.vue";
import { getDeviceProperties, getDeviceTypes } from "@/api/stations";

type SelectItem<T> = { text: string; value: T };

@Component({
  components: {
    EditFrame,
    CancelButton,
    DangerButton,
  },
})
export default class DeviceConfiguration extends Vue {
  @Prop()
  typeSettings!: DeviceTypeSettings;

  @Prop({ default: false })
  hideControls!: boolean;
  @Prop({ default: false }) editMode!: boolean;

  editedTypeSettings: DeviceTypeSettings | null = null;

  private deviceTypes: string[] = [];
  private loadingDeviceTypes = false;

  private deviceProperties: string[] = [];
  private loadingDeviceProperties = false;

  mounted(): void {
    this.importData();
    this.loadDeviceTypes();
    this.loadDeviceProperties();
  }

  @Watch("typeSettings")
  onTypeSettingsChanged(): void {
    this.importData();
    if (
      this.deviceTypes.length > 0 &&
      this.editedTypeSettings &&
      !this.editedTypeSettings.assignedType
    ) {
      this.editedTypeSettings.assignedType = this.deviceTypes[0];
    }
  }

  importData(): void {
    this.editedTypeSettings = deepClone(this.typeSettings);
  }

  get deviceTypesSelectItems(): SelectItem<string>[] {
    return this.deviceTypes.map((t) => ({ text: t, value: t }));
  }

  get devicePropertiesSelectItems(): SelectItem<string>[] {
    return this.deviceProperties.map((p) => ({ text: p, value: p }));
  }

  private async loadDeviceTypes(): Promise<void> {
    this.loadingDeviceTypes = true;
    try {
      const types = await getDeviceTypes();
      this.deviceTypes = Array.isArray(types) ? types : [];

      if (
        this.editedTypeSettings &&
        !this.editedTypeSettings.assignedType &&
        this.deviceTypes.length > 0
      ) {
        this.editedTypeSettings.assignedType = this.deviceTypes[0];
      }
    } catch (err) {
      this.deviceTypes = [];
      this.$emit("load-error", err);
    } finally {
      this.loadingDeviceTypes = false;
    }
  }

  private async loadDeviceProperties(): Promise<void> {
    this.loadingDeviceProperties = true;
    try {
      const props = await getDeviceProperties();
      this.deviceProperties = Array.isArray(props) ? props : [];
    } catch (err) {
      this.deviceProperties = [];
      this.$emit("load-error", err);
    } finally {
      this.loadingDeviceProperties = false;
    }
  }

  onAssignedTypeChange(value: any): void {
    if (this.editedTypeSettings) {
      this.editedTypeSettings.assignedType = value;
      this.$emit("input", this.editedTypeSettings);
    }
  }

  onAssignedPropertiesChange(value: any[]): void {
    if (this.editedTypeSettings) {
      this.editedTypeSettings.assignedProperties = value;
      this.$emit("input", this.editedTypeSettings);
    }
  }
}
